/**
 * Kanban Card Component
 * 
 * Renders account or contact cards within the kanban board using dedicated kanban card components.
 * Uses specialized kanban versions to avoid impacting existing page functionality.
 * 
 * Features:
 * - Uses KanbanAccountCard for entityType 2 (accounts)
 * - Uses KanbanContactCard for entityType 1 (contacts)  
 * - Maintains all core card functionality (navigation, notes, AI chat)
 * - Handles kanban-specific drag operations without interfering with existing cards
 * - Maintains search highlighting and optimized layout for kanban columns
 * - No delete functionality (view-only in kanban)
 * - Clean separation from main account/contact card components
 */

import { FC } from 'react';
import KanbanAccountCard from './KanbanAccountCard';
import KanbanContactCard from './KanbanContactCard';
import { Account } from '../../models/Account';
import { Contact } from '../../models/Contact';

interface KanbanItem {
  id: string;
  title: string;
  subtitle?: string;
  data: any;
  columnId: string;
}

interface KanbanCardProps {
  item: KanbanItem;
  entityType: number; // 1 for contacts, 2 for accounts
  searchQuery?: string;
  onDragStart: (id: string) => void;
  draggingId: string | null;
}

const KanbanCard: FC<KanbanCardProps> = ({ item, entityType, searchQuery = '', onDragStart, draggingId }) => {
  const isDragging = draggingId === item.id;

  // Create search keywords for highlighting
  const searchKeywords = searchQuery ? [searchQuery] : [];

  // Handle kanban drag start
  const handleKanbanDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onDragStart(item.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
  };

  const wrapperClasses = `
    transition-all duration-150 
    ${isDragging ? 'opacity-50' : ''}
  `;

  return (
    <div
      className={wrapperClasses}
      draggable
      onDragStart={handleKanbanDragStart}
    >
      {entityType === 2 ? (
        <KanbanAccountCard
          account={item.data as Account}
          keywords={searchKeywords}
        />
      ) : (
        <KanbanContactCard
          contact={item.data as Contact}
          keywords={searchKeywords}
        />
      )}
    </div>
  );
};

export default KanbanCard;